import React from 'react';
import { Order } from '@m4a3/api-client';
import { Tooltip } from '@mui/material';
import { useTimeTicker } from '../../../contexts/TimeTickerContext';
import { inactiveStatuses } from '../../../constants';

interface TimeUntilTakeoffBadgeProps {
  order: Order;
}

/**
 * TimeUntilTakeoffBadge - A performance-optimized component that subscribes to the global time ticker
 * to update the takeoff countdown without forcing the parent OrderCard to re-render.
 * 
 * This component:
 * - Subscribes to useTimeTicker() context
 * - Re-renders every second (only this component, not the parent)
 * - Shows time until takeoff with green/red indicator
 * - Hides for DISPATCHED, DELIVERED, and inactive orders
 */
export const TimeUntilTakeoffBadge: React.FC<TimeUntilTakeoffBadgeProps> = ({ order }) => {
  // Only THIS component subscribes to the time ticker
  const { currentTime } = useTimeTicker();

  // Calculate time until takeoff in minutes
  const getMinutesUntilTakeoff = (): number => {
    const expectedDeliveryTime = new Date(order.expectedDeliveryDate).getTime();
    const droneDeliveryDurationMs = 3 * 60 * 1000; // 3 minutes
    const takeoffTime = expectedDeliveryTime - droneDeliveryDurationMs;
    const timeDifference = takeoffTime - currentTime;
    return Math.floor(timeDifference / 1000 / 60);
  };

  const minutesUntilTakeoff = getMinutesUntilTakeoff();
  const isOverdue = minutesUntilTakeoff < 0;

  // Don't show badge for inactive, dispatched, or delivered orders
  if (inactiveStatuses.includes(order.status) || 
      order.status === 'DISPATCHED' || 
      order.status === 'DELIVERED') {
    return null;
  }

  return (
    <Tooltip 
      title={isOverdue 
        ? `${Math.abs(minutesUntilTakeoff)} minutes late for takeoff` 
        : `${Math.abs(minutesUntilTakeoff)} minutes until takeoff`
      } 
      arrow
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        backgroundColor: '#FFF',
        borderRadius: 5,
        border: '1px solid #D9DBE9',
        padding: '4px 8px',
        cursor: 'help'
      }}>
        <div style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: isOverdue ? '#EF4444' : '#10B981'
        }} />
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#344054',
          whiteSpace: 'nowrap'
        }}>
          {isOverdue ? `${Math.abs(minutesUntilTakeoff)} mins` : `${minutesUntilTakeoff} mins`}
        </span>
      </div>
    </Tooltip>
  );
};
